import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import StepShell from '@/components/StepShell';
import Ornament from '@/components/Ornament';
import { useSession } from '@/store/session';
import { postConsultation } from '@/lib/api';
import { parseShopifyCatalog } from '@/lib/catalog-parser';
import { recommend } from '@/lib/matching';
import type {
  ClientData,
  MouilletteScores,
  Preferences,
  Recommendation,
  SkinMeasures,
} from '@/types';

const QR_BASE =
  (import.meta.env.VITE_QR_LANDING_BASE as string | undefined) ?? 'https://resultado.elixirperfumes.es/r';

export default function Result() {
  const navigate = useNavigate();
  const session = useSession();
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'error'; message: string }
    | {
        kind: 'ready';
        recommendations: Recommendation[];
        couponCode: string;
        discount: number;
        landingUrl: string;
        qrDataUrl: string;
        offline?: boolean;
      }
  >({ kind: 'loading' });

  // Evita doble-submit en StrictMode dev
  const submittedRef = useRef(false);

  // Lista de campos faltantes con el paso al que llevan
  const missing = useMemo(() => {
    const out: Array<{ label: string; route: string }> = [];
    if (!session.client.name || !session.client.email) {
      out.push({ label: 'Datos del cliente', route: '/cliente' });
    } else if (
      !session.client.gender ||
      !session.client.ageBand ||
      !(session.client.occasions && session.client.occasions.length) ||
      !session.client.consentEmail
    ) {
      out.push({ label: 'Datos del cliente', route: '/cliente' });
    }
    if (!session.skin.ph || session.skin.tempC == null || !session.skin.type || !session.skin.texture) {
      out.push({ label: 'Mediciones de piel', route: '/piel' });
    }
    if (
      !session.preferences.q1_ambiente ||
      !session.preferences.q2_palabra ||
      !session.preferences.q3_estela ||
      !session.preferences.q4_dulce_seco ||
      !(session.preferences.q5_notas_familiares && session.preferences.q5_notas_familiares.length > 0) ||
      !session.preferences.q6_intensidad_habitual ||
      !session.preferences.q7_oriental_fresco ||
      !session.preferences.q8_estilo
    ) {
      out.push({ label: 'Preferencias', route: '/preferencias' });
    }
    if (Object.keys(session.mouillettes).length !== 5) {
      out.push({ label: 'Prueba olfativa', route: '/mouillettes' });
    }
    if (!session.consultationId) {
      out.unshift({ label: 'Iniciar consulta', route: '/' });
    }
    return out;
  }, [session]);

  const consultationReady = missing.length === 0;

  useEffect(() => {
    if (!consultationReady) {
      setState({ kind: 'error', message: 'consulta_incompleta' });
      return;
    }
    if (submittedRef.current) return;
    submittedRef.current = true;

    (async () => {
      const startedTs = session.startedAt ? Date.parse(session.startedAt) : Date.now();
      const durationS = Math.round((Date.now() - startedTs) / 1000);
      const consultationId = session.consultationId!;

      // 1) Intentamos el flujo normal contra el backend
      try {
        const res = await postConsultation({
          id: consultationId,
          storeId: session.storeId ?? 'unknown',
          sellerId: session.sellerId ?? undefined,
          client: session.client as ClientData,
          skin: session.skin as SkinMeasures,
          preferences: session.preferences as Preferences,
          mouillettes: session.mouillettes as MouilletteScores,
          durationS,
        });

        const landingUrl = `${QR_BASE}/${res.id}`;
        const qrDataUrl = await renderQr(landingUrl);

        session.setRecommendations(res.recommendations, res.couponCode);
        setState({
          kind: 'ready',
          recommendations: res.recommendations,
          couponCode: res.couponCode,
          discount: res.discount,
          landingUrl,
          qrDataUrl,
        });
        return;
      } catch (backendErr) {
        // 2) Fallback: catálogo directo + matching local
        try {
          const catalog = await fetchCatalogDirect();
          const recommendations = recommend({
            client: session.client as ClientData,
            skin: session.skin as SkinMeasures,
            preferences: session.preferences as Preferences,
            mouillettes: session.mouillettes as MouilletteScores,
            catalog,
          });

          const couponCode = `ELIXIR-EVENT-${shortHash(consultationId)}`;
          const landingUrl = `${QR_BASE}/${consultationId}`;
          const qrDataUrl = await renderQr(landingUrl);

          session.setRecommendations(recommendations, couponCode);
          setState({
            kind: 'ready',
            recommendations,
            couponCode,
            discount: 15,
            landingUrl,
            qrDataUrl,
            offline: true,
          });
        } catch (e2) {
          setState({
            kind: 'error',
            message: `${(backendErr as Error).message} · ${(e2 as Error).message}`,
          });
        }
      }
    })();
    // ejecutar una sola vez
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StepShell
      step={6}
      title="Tu diagnóstico"
      onBack={() => navigate('/mouillettes')}
      onNext={() => {
        session.resetConsultation();
        navigate('/');
      }}
      nextLabel="Finalizar"
    >
      {state.kind === 'loading' && (
        <div className="flex flex-col items-center justify-center py-16 text-elixir-cream/60">
          <div className="w-10 h-10 border-2 border-elixir-gold border-t-transparent rounded-full animate-spin mb-4" />
          <p>Cruzando tus datos con el catálogo…</p>
        </div>
      )}

      {state.kind === 'error' && state.message === 'consulta_incompleta' && (
        <div className="card max-w-xl mx-auto text-center">
          <h3 className="display text-2xl mb-2">Faltan algunos pasos</h3>
          <p className="text-sm text-elixir-cream/65 mb-5">
            Completa lo siguiente para que podamos generar tu diagnóstico:
          </p>
          <ul className="space-y-2 mb-6">
            {missing.map((m) => (
              <li key={m.route}>
                <button
                  onClick={() => navigate(m.route)}
                  className="w-full px-5 py-3 rounded-xl2 border border-elixir-gold/30 text-elixir-cream/85 hover:border-elixir-gold hover:bg-elixir-gold/10 transition flex items-center justify-between"
                >
                  <span className="serif text-lg">{m.label}</span>
                  <span className="text-elixir-gold text-sm tracking-widest uppercase">Ir →</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {state.kind === 'error' && state.message !== 'consulta_incompleta' && (
        <div className="card max-w-xl mx-auto text-center border-red-500/30">
          <h3 className="display text-2xl mb-2">No hemos podido conectar</h3>
          <p className="text-sm text-elixir-cream/65 mb-2">
            El servidor no respondió. Si estás en modo desarrollo local con
            <code className="mx-1 text-elixir-gold">npm run dev</code> el backend no está activo —
            despliega a Cloudflare o usa <code className="mx-1 text-elixir-gold">wrangler pages dev</code>.
          </p>
          <p className="text-xs text-elixir-cream/45 mt-4 break-all">{state.message}</p>
        </div>
      )}

      {state.kind === 'ready' && (
        <div className="space-y-8">
          {state.offline && (
            <div className="text-center text-[10px] tracking-[0.25em] uppercase text-elixir-cream/45 -mt-2">
              · modo demo · resultados sin guardar en servidor ·
            </div>
          )}

          {/* 5 fragancias */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {state.recommendations.map((r, idx) => (
              <RecommendationCard key={r.productId} rec={r} rank={idx + 1} />
            ))}
          </div>

          {/* Similares basadas en perfume favorito del cliente — solo si rellenó */}
          {session.client.favoritePerfume && (
            <SimilarBlock favoritePerfume={session.client.favoritePerfume} />
          )}

          <Ornament />

          {/* QR + cupón */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
            <div className="card text-center">
              <h3 className="display text-2xl mb-2 text-elixir-cream">Tu diagnóstico personal</h3>
              <p className="text-sm text-elixir-cream/65 mb-4">
                Escanea el código con tu móvil para llevarte el resultado completo y un cupón
                especial canjeable solo en tienda.
              </p>
              <img
                src={state.qrDataUrl}
                alt="QR de tu diagnóstico"
                className="mx-auto w-64 h-64 rounded-xl2 border border-elixir-gold/20"
              />
              <p className="mt-3 text-xs text-elixir-cream/45 break-all">{state.landingUrl}</p>
            </div>

            <div className="card">
              <h3 className="display text-2xl text-elixir-cream mb-2">
                Cupón <span className="text-elixir-gold">-{state.discount}%</span>
              </h3>
              <p className="text-sm text-elixir-cream/65 mb-4">
                Para canjear hoy mismo en tienda con tu vendedor.
              </p>
              <div className="bg-elixir-black/60 border-2 border-dashed border-elixir-gold/50 rounded-xl2 py-4 px-6 text-center">
                <p className="text-xs text-elixir-cream/55 uppercase tracking-widest mb-1">Código</p>
                <p className="display text-3xl text-elixir-gold tracking-wider">
                  {state.couponCode}
                </p>
              </div>
              <p className="text-xs text-elixir-cream/45 mt-3">
                Un solo uso. Solo válido en tiendas físicas Elixir. Caduca a 60 días.
              </p>
            </div>
          </div>
        </div>
      )}
    </StepShell>
  );
}

async function renderQr(url: string): Promise<string> {
  return QRCode.toDataURL(url, {
    margin: 1,
    width: 380,
    color: { dark: '#0B0B0B', light: '#F8F4EC' },
    errorCorrectionLevel: 'M',
  });
}

function shortHash(s: string): string {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h.toString(36).toUpperCase().slice(-6);
}

/**
 * Carga el catálogo directamente desde elixirperfumes.es/products.json.
 * En dev, Vite proxea esa ruta a https://elixirperfumes.es para esquivar CORS.
 * En producción tras deploy, normalmente este fallback no se ejecuta porque /api/catalog responde.
 */
async function fetchCatalogDirect() {
  const all: any[] = [];
  for (let page = 1; page < 20; page++) {
    const res = await fetch(`/products.json?limit=250&page=${page}`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error(`products.json ${res.status}`);
    const json = (await res.json()) as { products: any[] };
    if (!json.products?.length) break;
    all.push(...json.products);
    if (json.products.length < 250) break;
  }
  return parseShopifyCatalog({ products: all }, { storeBaseUrl: 'https://elixirperfumes.es' });
}

function RecommendationCard({ rec, rank }: { rec: Recommendation; rank: number }) {
  const p = rec.product;
  return (
    <article className="card relative flex flex-col">
      {/* Badge ranking */}
      <div className="absolute top-3 left-3 w-9 h-9 rounded-full bg-elixir-gold text-elixir-black flex items-center justify-center display text-lg shadow-gold">
        {rank}
      </div>
      {/* Badge match */}
      <div className="absolute top-3 right-3 px-3 py-1 rounded-full bg-elixir-emerald/80 text-elixir-cream text-xs font-semibold">
        {rec.matchPct}% match
      </div>

      {/* Imagen */}
      <div className="aspect-square bg-elixir-black/40 rounded-xl2 mb-3 overflow-hidden flex items-center justify-center">
        {p.imageUrl ? (
          <img
            src={p.imageUrl}
            alt={p.title}
            className="w-full h-full object-contain p-4"
            loading="lazy"
          />
        ) : (
          <span className="text-elixir-gold/40 text-5xl">◆</span>
        )}
      </div>

      <div className="flex-1">
        {p.brand && (
          <p className="text-xs text-elixir-gold uppercase tracking-widest mb-1">
            {p.brand}
            {p.isExclusive && <span className="ml-2 text-elixir-cream/50">· exclusivo</span>}
          </p>
        )}
        <h4 className="display text-xl text-elixir-cream leading-tight mb-1">{p.title}</h4>
        <div className="flex items-center gap-3 mb-3 text-xs">
          {p.family && (
            <span className="text-elixir-cream/55 capitalize">{p.family}</span>
          )}
          {(p as any).fragranticaRating && (
            <span className="text-elixir-gold-soft flex items-center gap-1">
              ★ {(p as any).fragranticaRating.toFixed(2)}
              {(p as any).fragranticaRatingCount && (
                <span className="text-elixir-cream/40">({(p as any).fragranticaRatingCount} votos)</span>
              )}
            </span>
          )}
        </div>

        {/* Acordes con color Fragantica si existen */}
        {(p as any).accordsColored && Array.isArray((p as any).accordsColored) && (p as any).accordsColored.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {(p as any).accordsColored.slice(0, 6).map((a: any, i: number) => (
              <span
                key={i}
                className="text-[10px] px-2 py-0.5 rounded-full font-medium"
                style={{
                  backgroundColor: a.color || '#666',
                  color: getContrastColor(a.color || '#666'),
                }}
              >
                {a.name}
              </span>
            ))}
          </div>
        )}

        {/* Razones */}
        {rec.reasons.length > 0 && (
          <ul className="text-xs text-elixir-cream/70 space-y-1 mb-3">
            {rec.reasons.slice(0, 3).map((r, i) => (
              <li key={i} className="flex gap-1.5">
                <span className="text-elixir-gold">·</span>
                <span>{r}</span>
              </li>
            ))}
          </ul>
        )}

        {/* Notas */}
        {(p.notesTop.length + p.notesHeart.length + p.notesBase.length) > 0 && (
          <div className="text-xs text-elixir-cream/55 space-y-0.5 mt-3 pt-3 border-t border-elixir-gold/15">
            {p.notesTop.length > 0 && (
              <p><span className="text-elixir-gold/70">Salida:</span> {p.notesTop.join(', ')}</p>
            )}
            {p.notesHeart.length > 0 && (
              <p><span className="text-elixir-gold/70">Corazón:</span> {p.notesHeart.join(', ')}</p>
            )}
            {p.notesBase.length > 0 && (
              <p><span className="text-elixir-gold/70">Fondo:</span> {p.notesBase.join(', ')}</p>
            )}
          </div>
        )}
      </div>

      {p.price != null && (
        <div className="mt-4 pt-3 border-t border-elixir-gold/15 flex items-baseline justify-between">
          <span className="display text-elixir-gold text-2xl">
            {p.price.toFixed(2)} {p.currency ?? 'EUR'}
          </span>
        </div>
      )}
    </article>
  );
}

/**
 * Bloque "Si te gusta tu perfume favorito, también podrías probar..."
 * Llama a /api/similar con el perfume que el cliente declaró usar.
 */
function SimilarBlock({ favoritePerfume }: { favoritePerfume: string }) {
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'error'; message: string }
    | { kind: 'ready'; recommendations: any[]; inputNotes: { top: string[]; heart: string[]; base: string[] } }
  >({ kind: 'loading' });

  useEffect(() => {
    let active = true;
    fetch('/api/similar', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ input: favoritePerfume }),
    })
      .then((r) => r.json() as Promise<any>)
      .then((d: any) => {
        if (!active) return;
        if (!d.ok || !d.recommendations?.length) {
          setState({ kind: 'error', message: d.error || 'no_results' });
          return;
        }
        setState({ kind: 'ready', recommendations: d.recommendations, inputNotes: d.input_notes });
      })
      .catch((e) => active && setState({ kind: 'error', message: e.message }));
    return () => {
      active = false;
    };
  }, [favoritePerfume]);

  if (state.kind === 'loading') {
    return (
      <div className="card text-center text-elixir-cream/60 text-sm py-6">
        Buscando perfumes similares a <span className="text-elixir-gold">{favoritePerfume}</span>…
      </div>
    );
  }
  if (state.kind === 'error') {
    return (
      <div className="card text-center text-elixir-cream/55 text-sm py-6">
        No encontramos <span className="text-elixir-gold">{favoritePerfume}</span> en nuestra base. Si quieres probar, escribe la marca + nombre exacto.
      </div>
    );
  }

  const inputNotesFlat = [
    ...state.inputNotes.top.slice(0, 3),
    ...state.inputNotes.heart.slice(0, 3),
    ...state.inputNotes.base.slice(0, 3),
  ];

  return (
    <div>
      <div className="text-center mb-4">
        <h3 className="display text-xl text-elixir-cream mb-1">
          Si te gusta <span className="text-elixir-gold">{favoritePerfume}</span>, también podrías probar…
        </h3>
        {inputNotesFlat.length > 0 && (
          <p className="text-xs text-elixir-cream/55">
            Notas detectadas: {inputNotesFlat.join(' · ')}
          </p>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {state.recommendations.slice(0, 3).map((rec: any) => (
          <article key={rec.productId} className="card relative flex flex-col">
            <div className="absolute top-3 right-3 px-3 py-1 rounded-full bg-elixir-gold/80 text-elixir-black text-xs font-semibold">
              {rec.similarity_pct}% similar
            </div>
            <div className="aspect-square bg-elixir-black/40 rounded-xl2 mb-3 overflow-hidden flex items-center justify-center">
              {rec.product.imageUrl ? (
                <img src={rec.product.imageUrl} alt={rec.product.title} className="w-full h-full object-contain p-4" loading="lazy" />
              ) : (
                <span className="text-elixir-gold/40 text-5xl">◆</span>
              )}
            </div>
            {rec.product.brand && (
              <p className="text-xs text-elixir-gold uppercase tracking-widest mb-1">{rec.product.brand}</p>
            )}
            <h4 className="display text-lg text-elixir-cream leading-tight mb-1">{rec.product.title}</h4>
            {rec.common_notes && rec.common_notes.length > 0 && (
              <p className="text-xs text-elixir-cream/55 mt-2">
                <span className="text-elixir-gold-soft">Notas en común:</span> {rec.common_notes.slice(0, 5).join(', ')}
              </p>
            )}
            {rec.product.price != null && (
              <div className="mt-3 pt-2 border-t border-elixir-gold/15">
                <span className="display text-elixir-gold text-xl">
                  {rec.product.price.toFixed(2)} {rec.product.currency ?? 'EUR'}
                </span>
              </div>
            )}
          </article>
        ))}
      </div>
    </div>
  );
}

/**
 * Helper: dado un color hex (ej "#FFFEC0"), devuelve "#000" o "#fff" según contraste.
 */
function getContrastColor(hex: string): string {
  const c = hex.replace('#', '');
  if (c.length !== 6) return '#000';
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  // Luminancia perceptual
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.55 ? '#1a1a1a' : '#fff';
}
